// The help cards' drawings. Staircase pictures run the real engine on a pretend patient
// (help/sim.ts) and draw the trials as they happen, again and again with new random draws.

import { useEffect, useMemo, useState } from "react";
import { DEFAULT_ACUITY, DEFAULT_CONTRAST, type AcuityConfig, type ContrastConfig } from "../lib/protocol";
import { pCorrect, simulateAcuity, simulateContrast, summarize, type Trace } from "./sim";
import type { HelpContext } from "./types";

export const COLOURS = ["#2a6fdb", "#d9480f", "#2b8a3e", "#9c36b5", "#c2255c", "#0b7285", "#a68b00", "#5c7cfa"];
const INK = "#1f2a35";
const MUTED = "#6b7785";
const W = 480;

const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** A frame counter that runs to `n`, holds, and starts again (round counts loops). */
function useTicker(n: number, ms = 70, hold = 35): { k: number; round: number } {
  const [s, setS] = useState({ k: reduced() ? n : 0, round: 0 });
  useEffect(() => {
    if (reduced()) return;
    const t = window.setInterval(() => setS((p) => (p.k >= n + hold ? { k: 0, round: p.round + 1 } : { k: p.k + 1, round: p.round })), ms);
    return () => window.clearInterval(t);
  }, [n, ms, hold]);
  return { k: Math.min(s.k, n), round: s.round };
}

export const TRUE_LOG_CS = 1.65;     // a typical healthy adult's log CS (Pelli-Robson 1.65-1.95)
export const TRUE_LOGMAR = 0.1;

type Highlight = "start" | "join" | "step" | "catch" | "reversal" | "stop" | "threshold" | "count" | "pick" | "range" | null;

export function contrastOf(ctx: HelpContext): ContrastConfig {
  return { ...DEFAULT_CONTRAST, ...(ctx.contrast ?? ctx.experiment?.contrast ?? {}) } as ContrastConfig;
}
export function acuityOf(ctx: HelpContext): AcuityConfig {
  return { ...DEFAULT_ACUITY, ...(ctx.acuity ?? ctx.experiment?.acuity ?? {}) } as AcuityConfig;
}

/** Interleaved contrast staircases on a pretend patient (true log CS 1.65), trial by trial. */
export function ContrastStaircases({ ctx, highlight = null, patched }: {
  ctx: HelpContext; highlight?: Highlight; patched?: Partial<ContrastConfig>;
}) {
  const cfg = useMemo(() => ({ ...contrastOf(ctx), ...patched, method: "staircases" as const }), [ctx, patched]);
  return <StaircasePlayer kind="contrast" run={(seed) => simulateContrast(cfg, TRUE_LOG_CS, seed)} truth={TRUE_LOG_CS}
    highlight={highlight} discard={cfg.staircase_type === "quest" ? 0 : cfg.discard_reversals}
    caption={(t) => `${t.trials} trials${t.catchTotal ? ` (${t.catchTotal} attention checks)` : ""}; estimate log CS ${fmt(t.estimate)}`
      + (t.spread !== null ? `, SD between staircases ${fmt(t.spread)}` : "")} />;
}

/** The acuity staircases on a pretend patient (true acuity 0.1 logMAR). */
export function AcuityStaircases({ ctx, highlight = null }: { ctx: HelpContext; highlight?: Highlight }) {
  const cfg = useMemo(() => acuityOf(ctx), [ctx]);
  return <StaircasePlayer kind="acuity" run={(seed) => simulateAcuity(cfg, TRUE_LOGMAR, seed)} truth={TRUE_LOGMAR}
    highlight={highlight} discard={0}
    caption={(t) => `${t.trials} trials${t.catchTotal ? ` (${t.catchTotal} attention checks)` : ""}; estimate ${fmt(t.estimate)} logMAR`} />;
}

const fmt = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? "—" : v.toFixed(d));

function StaircasePlayer({ kind, run, truth, highlight, discard, caption }: {
  kind: "contrast" | "acuity"; run: (seed: number) => Trace; truth: number; highlight: Highlight; discard: number;
  caption: (t: Trace) => string;
}) {
  const [round, setRound] = useState(0);
  const trace = useMemo(() => run(4242 + round * 101), [run, round]);
  const { k, round: r } = useTicker(trace.points.length);
  useEffect(() => { if (r !== round) setRound(r); }, [r, round]);
  return (
    <>
      <StaircaseChart trace={trace} kind={kind} truth={truth} upto={k} highlight={highlight} discard={discard} />
      <div className="small muted">Simulated patient ({kind === "contrast" ? `true log CS ${truth}` : `true acuity ${truth} logMAR`}):{" "}
        {caption(trace)}. Filled dot: right; open: wrong{trace.catchTotal ? "; grey diamond: attention check" : ""}.</div>
    </>
  );
}

export function StaircaseChart({ trace, kind, truth, upto, highlight, discard }: {
  trace: Trace; kind: "contrast" | "acuity"; truth: number; upto: number; highlight: Highlight; discard: number;
}) {
  const H = 210;
  const L = 40, R = 64, T = 14, B = 26;
  const pts = trace.points;
  const n = Math.max(pts.length, 10);
  const vals = pts.map((p) => p.value).concat([truth]);
  // harder is up: higher log CS (fainter), or lower logMAR (smaller)
  const sign = kind === "contrast" ? 1 : -1;
  const lo = Math.min(...vals.map((v) => v * sign)) - 0.1;
  const hi = Math.max(...vals.map((v) => v * sign)) + 0.1;
  const x = (i: number) => L + ((W - L - R) * (i + 0.5)) / n;
  const y = (v: number) => T + ((H - T - B) * (hi - v * sign)) / (hi - lo);
  const shown = pts.slice(0, upto);
  const byLabel = (lab: string) => shown.filter((p) => p.label === lab);
  const done = upto >= pts.length;
  const ticks: number[] = [];
  const step = (hi - lo) > 1.5 ? 0.5 : 0.25;
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(v * sign);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img"
      aria-label={`Simulated ${kind} staircases: ${trace.labels.length} staircases over ${pts.length} trials`}>
      <text x={8} y={T + 4} fontSize="10" fill={MUTED}>{kind === "contrast" ? "fainter ↑" : "smaller ↑"}</text>
      <text x={8} y={H - B} fontSize="10" fill={MUTED}>{kind === "contrast" ? "log CS" : "logMAR"}</text>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e3e8ee" />
          <text x={L - 4} y={y(v) + 3} fontSize="9" textAnchor="end" fill={MUTED}>{v.toFixed(2)}</text>
        </g>
      ))}
      <line x1={L} x2={W - R} y1={y(truth)} y2={y(truth)} stroke={INK} strokeDasharray="5 4" strokeWidth="1.2" />
      <text x={L + 3} y={y(truth) + 11} fontSize="9" fill={INK} opacity="0.8">patient's threshold (at the share right aimed for)</text>
      <text x={L} y={H - 6} fontSize="9" fill={MUTED}>trial 1</text>
      <text x={W - R} y={H - 6} fontSize="9" textAnchor="end" fill={MUTED}>trial {pts.length}</text>
      {trace.labels.map((lab, si) => {
        const c = COLOURS[si % COLOURS.length]!;
        const mine = byLabel(lab);
        const all = pts.filter((p) => p.label === lab);
        let revSeen = 0;
        return (
          <g key={lab}>
            {mine.length > 1 && <polyline fill="none" stroke={c} strokeWidth="1.4" opacity="0.75"
              points={mine.map((p) => `${x(p.i)},${y(p.value)}`).join(" ")} />}
            {mine.map((p, j) => {
              const rev = p.reversal ? ++revSeen : 0;
              const discarded = p.reversal && rev <= discard;
              return (
                <g key={p.i}>
                  <circle cx={x(p.i)} cy={y(p.value)} r={2.6} fill={p.correct ? c : "#fff"} stroke={c} strokeWidth="1.3" />
                  {p.reversal && (highlight === "reversal" || highlight === "threshold") && (
                    <circle cx={x(p.i)} cy={y(p.value)} r={6} fill="none" stroke={discarded ? "#adb5bd" : c} strokeWidth="1.2"
                      strokeDasharray={discarded ? "2 2" : undefined} />
                  )}
                  {j === 0 && (highlight === "start" || highlight === "join") && (
                    <>
                      <circle cx={x(p.i)} cy={y(p.value)} r={7} fill="none" stroke={c} strokeWidth="1.8" />
                      <text x={x(p.i) + 8} y={y(p.value) - 6} fontSize="9" fill={c}>{lab}</text>
                      {highlight === "join" && <line x1={x(p.i)} x2={x(p.i)} y1={T} y2={H - B} stroke={c} strokeDasharray="2 3" />}
                    </>
                  )}
                  {j === 1 && highlight === "step" && (
                    <path d={`M ${x(mine[0]!.i)} ${y(mine[0]!.value)} L ${x(mine[0]!.i)} ${y(p.value)}`} stroke={c}
                      strokeWidth="2.4" markerEnd="url(#hs-arrow)" />
                  )}
                </g>
              );
            })}
            {done && highlight === "stop" && all.length > 0 && (
              <rect x={x(all[all.length - 1]!.i) - 4} y={y(all[all.length - 1]!.value) - 4} width="8" height="8" fill="none" stroke={c} strokeWidth="1.6" />
            )}
            {done && trace.thresholds[lab] !== null && trace.thresholds[lab] !== undefined && (
              <g>
                <line x1={W - R + 6} x2={W - R + 22} y1={y(trace.thresholds[lab]!)} y2={y(trace.thresholds[lab]!)} stroke={c}
                  strokeWidth={highlight === "threshold" ? 3 : 2} />
                <text x={W - R + 25} y={y(trace.thresholds[lab]!) + 3} fontSize="8" fill={c}>{lab}</text>
              </g>
            )}
          </g>
        );
      })}
      {shown.filter((p) => p.catch).map((p) => (
        <rect key={`c${p.i}`} x={x(p.i) - (highlight === "catch" ? 4 : 3)} y={y(p.value) - (highlight === "catch" ? 4 : 3)}
          width={highlight === "catch" ? 8 : 6} height={highlight === "catch" ? 8 : 6} transform={`rotate(45 ${x(p.i)} ${y(p.value)})`}
          fill={p.correct ? "#868e96" : "#fff"} stroke="#868e96" />
      ))}
      {done && trace.estimate !== null && (
        <g>
          <line x1={W - R + 4} x2={W - 4} y1={y(trace.estimate)} y2={y(trace.estimate)} stroke={INK} strokeWidth="1.5" />
          <text x={W - 4} y={y(trace.estimate) + 12} fontSize="9" textAnchor="end" fill={INK}>mean {trace.estimate.toFixed(2)}</text>
        </g>
      )}
      <defs><marker id="hs-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
        <path d="M0,0 L8,4 L0,8 z" fill={INK} /></marker></defs>
    </svg>
  );
}

/** How long and how precise, over many simulated tests with these settings. */
export function SimNumbers({ ctx, kind, patched }: { ctx: HelpContext; kind: "contrast" | "acuity"; patched?: Partial<ContrastConfig> }) {
  const s = useMemo(() => {
    if (kind === "contrast") {
      const cfg = { ...contrastOf(ctx), ...patched, method: "staircases" as const };
      return summarize((seed) => simulateContrast(cfg, TRUE_LOG_CS, seed), TRUE_LOG_CS, 80);
    }
    const cfg = acuityOf(ctx);
    return summarize((seed) => simulateAcuity(cfg, TRUE_LOGMAR, seed), TRUE_LOGMAR, 80);
  }, [ctx, kind, patched]);
  return (
    <p className="small"><strong>With these settings</strong> (80 simulated tests): {s.meanTrials.toFixed(0)} trials per eye on
      average; the estimate's SD {s.sd.toFixed(3)} {kind === "contrast" ? "log units" : "logMAR"}
      {Math.abs(s.bias) >= 0.02 ? ` (bias ${s.bias > 0 ? "+" : ""}${s.bias.toFixed(2)})` : ""}; {s.pctCorrect.toFixed(0)}% of staircase
      trials answered right.</p>
  );
}

/** The psychometric function: chance (γ), lapses (δ) and the slope (β). */
export function PsychometricPicture({ beta, gamma, delta, mark, unit }: {
  beta: number; gamma: number; delta: number; mark: "beta" | "gamma" | "delta" | null; unit: string;
}) {
  const H = 190, L = 40, R = 14, T = 14, B = 30;
  const span = 0.5;
  const x = (u: number) => L + ((W - L - R) * (u + span)) / (2 * span);
  const y = (p: number) => T + (H - T - B) * (1 - p);
  const curve = (b: number) => Array.from({ length: 101 }, (_, i) => {
    const u = -span + (2 * span * i) / 100;
    return `${x(u)},${y(pCorrect(u, 0, b, gamma, delta))}`;
  }).join(" ");
  const { k } = useTicker(60, 60, 20);
  const alt = mark === "beta" ? beta * (0.5 + k / 60) : beta;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Chance of a right answer against how far the letter is from threshold">
      {[0, 0.25, 0.5, 0.75, 1].map((p) => (
        <g key={p}><line x1={L} x2={W - R} y1={y(p)} y2={y(p)} stroke="#e3e8ee" />
          <text x={L - 4} y={y(p) + 3} fontSize="9" textAnchor="end" fill={MUTED}>{p * 100}%</text></g>
      ))}
      <line x1={x(0)} x2={x(0)} y1={T} y2={H - B} stroke={MUTED} strokeDasharray="3 3" />
      <text x={x(0)} y={H - 16} fontSize="9" textAnchor="middle" fill={MUTED}>threshold</text>
      <text x={L} y={H - 4} fontSize="9" fill={MUTED}>easier ←</text>
      <text x={W - R} y={H - 4} fontSize="9" textAnchor="end" fill={MUTED}>→ harder ({unit} from threshold, ±{span})</text>
      {mark === "beta" && <polyline fill="none" stroke="#adb5bd" strokeWidth="1.5" strokeDasharray="4 3"
        points={Array.from({ length: 101 }, (_, i) => { const u = -span + (2 * span * i) / 100; return `${x(-u)},${y(pCorrect(u, 0, alt, gamma, delta))}`; }).join(" ")} />}
      <polyline fill="none" stroke={COLOURS[0]} strokeWidth="2.2"
        points={curve(beta).split(" ").map((pt) => { const [a, b] = pt.split(","); return `${2 * x(0) - Number(a)},${b}`; }).join(" ")} />
      <line x1={L} x2={W - R} y1={y(gamma)} y2={y(gamma)} stroke={mark === "gamma" ? "#d9480f" : "#ced4da"} strokeWidth={mark === "gamma" ? 2 : 1} />
      <text x={W - R - 2} y={y(gamma) - 4} fontSize="9" textAnchor="end" fill={mark === "gamma" ? "#d9480f" : MUTED}>γ = {gamma} (guessing)</text>
      <line x1={L} x2={W - R} y1={y(1 - delta)} y2={y(1 - delta)} stroke={mark === "delta" ? "#d9480f" : "#ced4da"} strokeWidth={mark === "delta" ? 2 : 1} />
      <text x={L + 4} y={y(1 - delta) + 11} fontSize="9" fill={mark === "delta" ? "#d9480f" : MUTED}>1 − δ = {(1 - delta).toFixed(2)} (lapses)</text>
      {mark === "beta" && <text x={x(0) + 6} y={y(0.62)} fontSize="9" fill={MUTED}>dashed: β = {alt.toFixed(1)}</text>}
    </svg>
  );
}

/** QUEST's prior: a bell curve over where the threshold might be; each answer narrows it. */
export function PriorPicture({ mean, sd, lo, hi, unit }: { mean: number; sd: number; lo: number; hi: number; unit: string }) {
  const H = 170, L = 20, R = 20, T = 14, B = 30;
  const { k } = useTicker(40, 90, 20);
  const shrink = 1 / (1 + k / 8);
  const x = (v: number) => L + ((W - L - R) * (v - lo)) / (hi - lo);
  const g = (s: number) => Array.from({ length: 121 }, (_, i) => {
    const v = lo + ((hi - lo) * i) / 120;
    const d = Math.exp(-0.5 * ((v - mean) / s) ** 2);
    return `${x(v)},${T + (H - T - B) * (1 - d)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Prior: centred on ${mean} ${unit}, SD ${sd}`}>
      <line x1={L} x2={W - R} y1={H - B} y2={H - B} stroke={MUTED} />
      <polyline fill="none" stroke={COLOURS[0]} strokeWidth="2" points={g(sd)} />
      <polyline fill="none" stroke={COLOURS[1]} strokeWidth="1.5" strokeDasharray="4 3" points={g(sd * shrink)} />
      <line x1={x(mean)} x2={x(mean)} y1={T} y2={H - B} stroke={INK} strokeDasharray="3 3" />
      <text x={x(mean)} y={H - 16} fontSize="9" textAnchor="middle" fill={INK}>prior mean {mean}</text>
      <text x={L} y={H - 4} fontSize="9" fill={MUTED}>{lo} {unit}</text>
      <text x={W - R} y={H - 4} fontSize="9" textAnchor="end" fill={MUTED}>{hi} {unit}</text>
      <text x={W - R} y={T + 8} fontSize="9" textAnchor="end" fill={COLOURS[1]}>dashed: after answers, narrower</text>
    </svg>
  );
}

/** A plain E (5 strokes), prongs to the right, at (x, y) with height h. */
export function E({ x, y, h, fill = INK, rot = 0 }: { x: number; y: number; h: number; fill?: string; rot?: number }) {
  const s = h / 5;
  return (
    <g transform={`rotate(${rot} ${x + h / 2} ${y + h / 2})`} fill={fill}>
      <rect x={x} y={y} width={s} height={h} />
      <rect x={x} y={y} width={h} height={s} /><rect x={x} y={y + 2 * s} width={h} height={s} /><rect x={x} y={y + 4 * s} width={h} height={s} />
    </g>
  );
}

/** Contrast letters' size: the chart's 2.8°, or the eye's acuity plus an offset. */
export function LetterSizePicture({ rule, offset }: { rule: "pelli_robson" | "acuity_relative"; offset: number }) {
  const H = 150;
  const ac = 0.3;                                                 // an example acuity
  const pr = 70;                                                  // drawn height for 2.8°
  const acH = 12 * 10 ** ac;                                      // drawn height at the acuity
  const rel = acH * 10 ** offset;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Letter sizes: the chart's 2.8 degrees, and acuity plus the offset">
      <g opacity={rule === "pelli_robson" ? 1 : 0.35}>
        <E x={30} y={H - 20 - pr} h={pr} />
        <text x={30} y={H - 6} fontSize="10" fill={INK}>2.8° (the chart at 1 m)</text>
      </g>
      <g opacity={rule === "acuity_relative" ? 1 : 0.35}>
        <E x={220} y={H - 20 - acH} h={acH} fill={MUTED} />
        <text x={205} y={H - 6} fontSize="10" fill={MUTED}>acuity {ac}</text>
        <E x={300} y={H - 20 - rel} h={rel} />
        <text x={300} y={H - 6} fontSize="10" fill={INK}>acuity + {offset} logMAR (×{(10 ** offset).toFixed(1)})</text>
      </g>
    </svg>
  );
}

/** The size check before contrast: Es at full contrast; any wrong, they grow. */
export function GatePicture({ trials, stepLogmar }: { trials: number; stepLogmar: number }) {
  const H = 120;
  const { k } = useTicker(3, 900, 2);
  const h = 22 * 10 ** (stepLogmar * k);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Size check: ${trials} letters at full contrast, larger after a miss`}>
      {Array.from({ length: Math.min(trials, 8) }, (_, i) => (
        <E key={i} x={20 + i * (h + 14)} y={(H - h) / 2 - 8} h={h} rot={[0, 90, 180, 270][i % 4]} />
      ))}
      <text x={20} y={H - 6} fontSize="10" fill={MUTED}>attempt {k + 1}: letters {k === 0 ? "at the test size" : `+${(stepLogmar * k).toFixed(2)} logMAR`}</text>
    </svg>
  );
}

/** The chart's descending levels: groups of letters, each group fainter. */
export function ChartLevelsPicture({ step, max, group }: { step: number; max: number; group: number }) {
  const H = 130;
  const levels = Math.floor(max / step + 1e-9) + 1;
  const shown = Math.min(levels, 8);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${levels} levels, ${step} log CS apart, ${group} letters each`}>
      {Array.from({ length: shown }, (_, i) => {
        const cs = i * step;
        const grey = Math.round(255 * (1 - 10 ** -cs));
        return (
          <g key={i}>
            {Array.from({ length: Math.min(group, 5) }, (_, j) => (
              <E key={j} x={14 + i * 57 + (j % 3) * 15} y={20 + Math.floor(j / 3) * 18} h={12} fill={`rgb(${grey},${grey},${grey})`} rot={[0, 90, 180, 270, 90][j]} />
            ))}
            <text x={14 + i * 57} y={H - 30} fontSize="9" fill={MUTED}>{cs.toFixed(2)}</text>
          </g>
        );
      })}
      <text x={14} y={H - 10} fontSize="10" fill={MUTED}>log CS of each level (first {shown} of {levels}); {group} letters per level</text>
    </svg>
  );
}

/** Luminance contrast (a darker shade of the background) or a fade toward the letter colour. */
export function ContrastModePicture({ mode, bg, letter }: { mode: "luminance" | "toward_letter"; bg: [number, number, number]; letter: [number, number, number] }) {
  const H = 110;
  const toCss = (rgb: number[]) => `rgb(${rgb.map((v) => Math.round(255 * Math.min(1, Math.max(0, v)) ** (1 / 2.2))).join(",")})`;
  const steps = [1, 0.5, 0.25, 0.12, 0.06];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={mode === "luminance" ? "Letters as darker shades of the background" : "Letters fading from the letter colour into the background"}>
      <rect x={0} y={0} width={W} height={H} fill={toCss(bg)} />
      {steps.map((c, i) => {
        const col = mode === "luminance" ? bg.map((v) => v * (1 - c)) : bg.map((v, j) => v + (letter[j]! - v) * c);
        return (
          <g key={i}>
            <E x={22 + i * 90} y={20} h={50} fill={toCss(col)} />
            <text x={22 + i * 90} y={H - 10} fontSize="10" fill={MUTED}>{mode === "luminance" ? `Weber ${c}` : `mix ${c}`}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Jittered colour blobs in a band along the E's edges. */
export function JitterPicture({ inner, outer, blob, density, intensity, every, colours }: {
  inner: number; outer: number; blob: number; density: number; intensity: number; every: number;
  colours: [string, string];
}) {
  const H = 190;
  const h = 150;
  const s = h / 5;
  const x0 = 165, y0 = 20;
  const { k } = useTicker(1000, 16 * Math.max(1, every) * 4, 0);
  const blobs = useMemo(() => {
    let a = (k * 2654435761) >>> 0;
    const rnd = () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
    const rects: [number, number, number, number][] = [[0, 0, s, h], [0, 0, h, s], [0, 2 * s, h, s], [0, 4 * s, h, s]];
    const inside = (px: number, py: number) => rects.some(([rx, ry, rw, rh]) => px >= rx && px <= rx + rw && py >= ry && py <= ry + rh);
    const dist = (px: number, py: number) => {
      // distance to the nearest edge of the E (sampled)
      let best = Infinity;
      for (const [rx, ry, rw, rh] of rects) {
        const dx = Math.max(rx - px, 0, px - rx - rw);
        const dy = Math.max(ry - py, 0, py - ry - rh);
        const out = Math.hypot(dx, dy);
        const inn = Math.min(px - rx, rx + rw - px, py - ry, ry + rh - py);
        best = Math.min(best, out > 0 ? out : -inn);
      }
      return best;
    };
    const out: Array<{ x: number; y: number; c: number }> = [];
    const g = Math.max(3, blob * s);
    for (let py = -outer * s; py < h + outer * s; py += g) {
      for (let px = -outer * s; px < h + outer * s; px += g) {
        const d = dist(px + g / 2, py + g / 2);
        const isIn = inside(px + g / 2, py + g / 2);
        if ((isIn && -d <= inner * s) || (!isIn && d <= outer * s)) {
          if (rnd() < density) out.push({ x: px, y: py, c: rnd() < 0.5 ? 0 : 1 });
        }
      }
    }
    return { out, g };
  }, [k, inner, outer, blob, density, s, h]);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="An E with flickering colour blobs along its edges">
      <rect x={0} y={0} width={W} height={H} fill="#c8c8c8" />
      <E x={x0} y={y0} h={h} fill="#555" />
      {blobs.out.map((b, i) => (
        <rect key={i} x={x0 + b.x} y={y0 + b.y} width={blobs.g} height={blobs.g} fill={colours[b.c]} opacity={intensity} />
      ))}
      <text x={8} y={H - 8} fontSize="10" fill={INK}>band: {inner} inside + {outer} outside; blobs {blob} stroke; {Math.round(density * 100)}% coloured; new pattern every {every} frame{every === 1 ? "" : "s"}</text>
    </svg>
  );
}

/** A trial's timeline: letter shown, answer window, blank gap. */
export function TimingPicture({ stimulus, timeout, isi }: { stimulus: number; timeout: number; isi: number }) {
  const H = 110, L = 14;
  const total = Math.max(stimulus || 1500, timeout || 2500) + isi + 400;
  const x = (ms: number) => L + ((W - 2 * L) * ms) / total;
  const { k } = useTicker(60, 50, 10);
  const now = (k / 60) * total;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Timeline of one trial">
      <rect x={x(0)} y={22} width={x(stimulus || total - isi - 400) - x(0)} height={22} fill={COLOURS[0]} opacity="0.85" />
      <text x={x(0) + 4} y={37} fontSize="10" fill="#fff">letter {stimulus ? `${stimulus} ms` : "until answered"}</text>
      <rect x={x(0)} y={50} width={x(timeout || total - isi - 400) - x(0)} height={16} fill={COLOURS[2]} opacity="0.6" />
      <text x={x(0) + 4} y={62} fontSize="10" fill={INK}>answer window {timeout ? `${timeout} ms, then counted wrong` : "no limit"}</text>
      <rect x={x(total - isi - 400)} y={22} width={x(isi) - x(0)} height={44} fill="#dee2e6" />
      <text x={x(total - isi - 400) + 4} y={84} fontSize="10" fill={MUTED}>blank {isi} ms after the answer</text>
      <line x1={x(now)} x2={x(now)} y1={14} y2={74} stroke="#d9480f" strokeWidth="2" />
      <text x={L} y={H - 6} fontSize="9" fill={MUTED}>one trial, left to right</text>
    </svg>
  );
}

/** The order of a session's tests: eye by eye, or step by step. */
export function EyeOrderPicture({ order, eyes, steps }: { order: "eye_by_eye" | "step_by_step"; eyes: string[]; steps: string[] }) {
  const H = 90;
  const seq: Array<[string, string]> = order === "eye_by_eye"
    ? eyes.flatMap((e) => steps.map((s) => [e, s] as [string, string]))
    : steps.flatMap((s) => eyes.map((e) => [e, s] as [string, string]));
  const w = Math.min(70, (W - 20) / Math.max(1, seq.length) - 4);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Order: ${seq.map(([e, s]) => `${e} ${s}`).join(", ")}`}>
      {seq.map(([e, s], i) => (
        <g key={i}>
          <rect x={10 + i * (w + 4)} y={18} width={w} height={40} rx={5} fill={e === "OD" ? "#dbe7fb" : e === "OS" ? "#fde2d5" : "#e3f1e4"} stroke="#adb5bd" />
          <text x={10 + i * (w + 4) + w / 2} y={34} fontSize="10" textAnchor="middle" fill={INK}>{e}</text>
          <text x={10 + i * (w + 4) + w / 2} y={50} fontSize="9" textAnchor="middle" fill={MUTED}>{s.slice(0, 10)}</text>
        </g>
      ))}
      <text x={10} y={H - 8} fontSize="10" fill={MUTED}>{order === "eye_by_eye" ? "every step for one eye, then the other" : "each step for both eyes, then the next step"}</text>
    </svg>
  );
}

/** The same letter is a smaller angle farther away. */
export function DistancePicture({ mm }: { mm: number }) {
  const H = 120;
  const d = Math.min(380, 80 + (mm / 4000) * 300);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Viewing distance ${mm / 1000} m`}>
      <rect x={20} y={20} width={8} height={80} fill={INK} />
      <circle cx={30 + d} cy={60} r={9} fill="#f2d4b8" stroke="#8a6a50" />
      <line x1={30} x2={30 + d} y1={45} y2={60} stroke={COLOURS[0]} />
      <line x1={30} x2={30 + d} y1={75} y2={60} stroke={COLOURS[0]} />
      <text x={40 + d / 2} y={110} fontSize="10" textAnchor="middle" fill={INK}>{(mm / 1000).toFixed(2)} m</text>
    </svg>
  );
}

/** A schedule on a calendar strip: done, due, grace, overdue. */
export function SchedulePicture({ kind, n, unit, grace }: { kind: string; n: number; unit: string; grace: number }) {
  const H = 100;
  const days = 42;
  const per = unit === "days" ? n : unit === "weeks" ? 7 * n : 30 * n;
  const x = (d: number) => 14 + ((W - 28) * d) / days;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Schedule on a six-week strip">
      <line x1={x(0)} x2={x(days)} y1={50} y2={50} stroke="#adb5bd" strokeWidth="2" />
      {Array.from({ length: days + 1 }, (_, d) => d % 7 === 0 && (
        <text key={d} x={x(d)} y={70} fontSize="9" textAnchor="middle" fill={MUTED}>week {d / 7}</text>))}
      {kind === "interval" ? (
        <>
          <circle cx={x(0)} cy={50} r={6} fill={COLOURS[2]} />
          <text x={x(0)} y={36} fontSize="9" textAnchor="middle" fill={COLOURS[2]}>done</text>
          {per <= days && <><rect x={x(per)} y={42} width={Math.max(2, x(per + grace) - x(per))} height={16} fill="#ffe8cc" />
            <circle cx={x(per)} cy={50} r={6} fill="#fff" stroke="#d9480f" strokeWidth="2" />
            <text x={x(per)} y={36} fontSize="9" textAnchor="middle" fill="#d9480f">due</text>
            {per + grace + 1 <= days && <text x={x(per + grace + 1)} y={90} fontSize="9" fill="#c92a2a">overdue after {grace} grace day{grace === 1 ? "" : "s"}</text>}</>}
        </>
      ) : kind === "every_n_sessions" ? (
        Array.from({ length: 6 }, (_, i) => (
          <g key={i}><circle cx={x(i * 7 + 2)} cy={50} r={6} fill={i % n === 0 ? COLOURS[2] : "#fff"} stroke={COLOURS[2]} />
            <text x={x(i * 7 + 2)} y={36} fontSize="9" textAnchor="middle" fill={MUTED}>visit {i + 1}</text></g>
        ))
      ) : (
        <text x={14} y={30} fontSize="11" fill={MUTED}>whenever the patient wants: never due, never overdue</text>
      )}
    </svg>
  );
}

/** Mandatory, recommended, not required: what the patient meets. */
export function LevelsPicture() {
  const H = 120;
  const col = (x: number, title: string, colour: string, lines: string[]) => (
    <g>
      <rect x={x} y={8} width={148} height={104} rx={8} fill="#fff" stroke={colour} strokeWidth="2" />
      <text x={x + 10} y={28} fontSize="12" fontWeight="bold" fill={colour}>{title}</text>
      {lines.map((l, i) => <text key={i} x={x + 10} y={48 + i * 16} fontSize="10" fill={INK}>{l}</text>)}
    </g>
  );
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Mandatory blocks until it passes; recommended can be skipped with a note; not required is not asked">
      {col(6, "Mandatory", "#b42318", ["must pass", "no test until then", "fix steps shown"])}
      {col(166, "Recommended", "#d97706", ["fix steps shown", "patient may go on", "results get a note"])}
      {col(326, "Not required", "#6b7785", ["not asked", "screen checks still", "run and recorded"])}
    </svg>
  );
}
